
import { createContext, useContext, useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Subscription, SubscriptionPlan } from '@/types/subscription';

interface AuthContextProps {
  session: Session | null;
  user: User | null;
  profile: any | null;
  isLoading: boolean;
  credits: number;
  subscription: Subscription | null;
  subscriptionPlan: SubscriptionPlan | null;
  hasActiveSubscription: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps>({
  session: null,
  user: null,
  profile: null,
  isLoading: true,
  credits: 0,
  subscription: null,
  subscriptionPlan: null,
  hasActiveSubscription: false,
  isAdmin: false,
  signOut: async () => {},
  refreshProfile: async () => {},
  refreshSubscription: async () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: React.ReactNode;
}

// List of admin emails - hardcoded for direct checking
const ADMIN_EMAILS = ['bw@expat-savvy.ch', 'chregu@unterwaldner.com'];

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [credits, setCredits] = useState<number>(0);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlan | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check if a user is an admin based on email
  const checkIsAdmin = (email: string | undefined) => {
    if (!email) return false;
    return ADMIN_EMAILS.includes(email.toLowerCase());
  };

  useEffect(() => {
    // Set up auth state listener FIRST to avoid missing events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        // Check admin status immediately based on email
        if (currentSession?.user?.email) {
          const adminStatus = checkIsAdmin(currentSession.user.email);
          console.log(`Auth state change: User ${currentSession.user.email} admin status:`, adminStatus);
          setIsAdmin(adminStatus);
        } else {
          setIsAdmin(false);
        }
        
        // Fetch user profile if logged in
        if (currentSession?.user) {
          // Use setTimeout to prevent deadlocks
          setTimeout(() => {
            fetchUserProfile(currentSession.user.id);
            fetchSubscriptionInfo(currentSession.user.id);
          }, 0);
        } else {
          setProfile(null);
          setCredits(0);
          setSubscription(null);
          setSubscriptionPlan(null);
          setHasActiveSubscription(false);
          setIsAdmin(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      // Check admin status immediately based on email
      if (currentSession?.user?.email) {
        const adminStatus = checkIsAdmin(currentSession.user.email);
        console.log(`Initial session: User ${currentSession.user.email} admin status:`, adminStatus);
        setIsAdmin(adminStatus);
      }
      
      if (currentSession?.user) {
        fetchUserProfile(currentSession.user.id);
        fetchSubscriptionInfo(currentSession.user.id);
      }
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return;
      }
      
      setProfile(data);
      // Updated to safely handle if credits doesn't exist yet
      setCredits(data.credits || 0);

      // Double check admin status using email
      if (user?.email) {
        const adminStatus = checkIsAdmin(user.email);
        console.log(`Profile fetch: User ${user.email} admin status:`, adminStatus);
        setIsAdmin(adminStatus);
      }
    } catch (error) {
      console.error('Error in profile fetch:', error);
    }
  };

  const fetchSubscriptionInfo = async (userId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        method: 'GET',
      });
      
      if (error) {
        console.error('Error checking subscription:', error);
        return;
      }

      setCredits(data.credits || 0);
      setHasActiveSubscription(data.hasActiveSubscription || false);
      setSubscription(data.subscription || null);
      setSubscriptionPlan(data.subscriptionPlan || null);
      
      // Only update isAdmin if it came from the backend and the user has the proper email
      if (data.isAdmin && user?.email && checkIsAdmin(user.email)) {
        console.log(`Subscription check: Confirmed admin status for ${user.email}`);
        setIsAdmin(true);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  // Function to manually check admin status - useful for debugging
  const refreshAdminStatus = () => {
    if (user?.email) {
      const adminStatus = checkIsAdmin(user.email);
      console.log(`Manual refresh: User ${user.email} admin status:`, adminStatus);
      setIsAdmin(adminStatus);
      return adminStatus;
    }
    return false;
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchUserProfile(user.id);
      refreshAdminStatus();
    }
  };

  const refreshSubscription = async () => {
    if (user) {
      await fetchSubscriptionInfo(user.id);
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: 'Abgemeldet',
        description: 'Sie wurden erfolgreich abgemeldet.',
      });
      navigate('/');
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: 'Abmeldung fehlgeschlagen.',
        variant: 'destructive',
      });
    }
  };

  // Debug logging for admin status
  useEffect(() => {
    console.log('AUTH CONTEXT - Current admin status:', isAdmin);
    console.log('AUTH CONTEXT - Current user email:', user?.email);
  }, [isAdmin, user?.email]);

  const value = {
    session,
    user,
    profile,
    isLoading,
    credits,
    subscription,
    subscriptionPlan,
    hasActiveSubscription,
    isAdmin,
    signOut,
    refreshProfile,
    refreshSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
